import { hasParentAccess } from "@/features/esg/utils/org-scope.utils";
import { buildPickerOptions } from "./hierarchy-picker";

export const ALL = "__all__";
export const ALL_CATEGORIES = [
  "General",
  "Environmental",
  "Social",
  "Governance",
];
export const HIERARCHIES = [
  { value: "all", label: "All" },
  { value: "businessUnit", label: "Business Unit" },
  { value: "company", label: "Company" },
  { value: "site", label: "Site" },
  { value: "country", label: "Country" },
];

export const singleToArray = (value) =>
  value === ALL || !value ? [] : [value];

export const arrayToSingle = (values) =>
  values.length === 1 ? values[0] : ALL;

export const seedDashboardScopeFilters = (filters = {}) => ({
  hierarchy: filters.hierarchy || "all",
  orgIds: Array.isArray(filters.orgIds) ? [...filters.orgIds] : [],
  years: Array.isArray(filters.years) ? [...filters.years] : [],
  frameworks: Array.isArray(filters.frameworks) ? [...filters.frameworks] : [],
  categories: Array.isArray(filters.categories) ? [...filters.categories] : [],
});

export const countActiveDashboardScopeFilters = (filters = {}) => {
  let count = 0;
  if (Array.isArray(filters.years) && filters.years.length) count += 1;
  if (Array.isArray(filters.frameworks) && filters.frameworks.length) {
    count += 1;
  }
  if (Array.isArray(filters.categories) && filters.categories.length) {
    count += 1;
  }
  if (Array.isArray(filters.orgIds) && filters.orgIds.length) count += 1;
  return count;
};

export const buildYearOptions = (years = [], t) => [
  { value: ALL, label: t("All Years") },
  ...years
    .slice()
    .sort((left, right) => right - left)
    .map((year) => ({ value: String(year), label: String(year) })),
];

export const buildFrameworkOptions = (frameworks = [], t) => [
  { value: ALL, label: t("All Frameworks") },
  ...frameworks.map((framework) => ({
    value: framework,
    label: framework,
  })),
];

export const buildCategoryOptions = ({ user, t }) => {
  const allowedCategories = hasParentAccess(user)
    ? ALL_CATEGORIES
    : ALL_CATEGORIES.filter((category) => category !== "General");

  return [
    { value: ALL, label: t("All Categories") },
    ...allowedCategories.map((category) => ({
      value: category,
      label: t(category),
    })),
  ];
};

export const buildHierarchyOptions = (orgTree = [], t) =>
  HIERARCHIES.filter(
    (hierarchy) =>
      hierarchy.value === "all" ||
      buildPickerOptions(orgTree, hierarchy.value).length > 0,
  ).map((hierarchy) => ({
    value: hierarchy.value,
    label: t(hierarchy.label),
  }));

const getNodeId = (node) =>
  node?.id === null || node?.id === undefined ? "" : String(node.id);

const flattenNodes = (nodes = [], out = []) => {
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    out.push(node);
    if (node?.children?.length) flattenNodes(node.children, out);
  });
  return out;
};

const flatNodes = (nodes = []) =>
  nodes.map((node) => ({ ...node, children: [] }));

const buildCountryPickerOptions = (nodes = []) => {
  const seen = new Set();

  return nodes
    .map((node) => node?.country)
    .filter((country) => {
      if (!country || seen.has(country)) return false;
      seen.add(country);
      return true;
    })
    .sort()
    .map((country) => ({
      id: `country:${country}`,
      name: country,
      unitLevel: "country",
      country,
      children: [],
    }));
};

const buildAssignedOnlyPickerOptions = (orgTree, hierarchy, assignedIds) => {
  const assignedIdSet = new Set((assignedIds || []).map(String));
  const assignedNodes = flattenNodes(orgTree).filter((node) => {
    const nodeId = getNodeId(node);
    return nodeId && assignedIdSet.has(nodeId);
  });

  switch (hierarchy) {
    case "company":
      return flatNodes(
        assignedNodes.filter((node) => node?.unitLevel === "level_2"),
      );
    case "businessUnit":
      return flatNodes(
        assignedNodes.filter((node) => node?.unitLevel === "level_1"),
      );
    case "site":
      return flatNodes(
        assignedNodes.filter((node) => node?.unitLevel === "level_3"),
      );
    case "country":
      return buildCountryPickerOptions(assignedNodes);
    case "all":
    default:
      return buildPickerOptions(orgTree, hierarchy);
  }
};

export const buildOrganizationPickerTree = (
  orgTree = [],
  hierarchy = "all",
  assignedIds = null,
) => {
  if (hierarchy !== "all" && Array.isArray(assignedIds)) {
    return buildAssignedOnlyPickerOptions(orgTree, hierarchy, assignedIds);
  }

  return buildPickerOptions(orgTree, hierarchy);
};

const getReportingYearOrganizationId = (organization) => {
  const id =
    organization?._id ??
    organization?.id ??
    organization?.organizationId ??
    organization;

  return id === null || id === undefined ? "" : String(id);
};

const getReportingYearOrganizationIds = (reportingYear, companyId) => [
  ...(reportingYear?.isCompanySelected && companyId ? [companyId] : []),
  ...(Array.isArray(reportingYear?.organizations)
    ? reportingYear.organizations
    : []
  ).map(getReportingYearOrganizationId),
];

const getScopedReportingYears = (reportingYears = [], selectedYears = []) => {
  if (!Array.isArray(reportingYears) || reportingYears.length === 0) return [];
  if (!Array.isArray(selectedYears) || selectedYears.length !== 1) {
    return reportingYears;
  }

  const selectedYear = Number(selectedYears[0]);
  if (!Number.isFinite(selectedYear)) return reportingYears;

  return reportingYears.filter(
    (reportingYear) => Number(reportingYear?.startYear) === selectedYear,
  );
};

export const getScopedReportingYearOrganizationIds = ({
  reportingYears = [],
  selectedYears = [],
  companyId,
} = {}) => {
  const scopedReportingYears = getScopedReportingYears(
    reportingYears,
    selectedYears,
  );

  if (scopedReportingYears.length === 0) return null;
  if (scopedReportingYears.some((item) => item?.isAllOrganization)) {
    return null;
  }

  return [
    ...new Set(
      scopedReportingYears
        .flatMap((item) => getReportingYearOrganizationIds(item, companyId))
        .filter(Boolean),
    ),
  ];
};

const filterTreeByAllowedOrganizationIds = (nodes = [], allowedIds) =>
  (Array.isArray(nodes) ? nodes : [])
    .map((node) => {
      const nodeId = getNodeId(node);
      const children = filterTreeByAllowedOrganizationIds(
        node?.children || [],
        allowedIds,
      );
      const isAllowed = nodeId && allowedIds.has(nodeId);

      if (!isAllowed && children.length === 0) return null;

      return {
        ...node,
        children,
      };
    })
    .filter(Boolean);

export const scopeOrgTreeByReportingYears = ({
  orgTree = [],
  reportingYears = [],
  selectedYears = [],
  companyId,
} = {}) => {
  const scopedReportingYears = getScopedReportingYears(
    reportingYears,
    selectedYears,
  );

  if (scopedReportingYears.length === 0) return orgTree;
  if (scopedReportingYears.some((item) => item?.isAllOrganization)) {
    return orgTree;
  }

  const allowedIds = new Set(
    getScopedReportingYearOrganizationIds({
      reportingYears,
      selectedYears,
      companyId,
    }) || [],
  );

  return filterTreeByAllowedOrganizationIds(orgTree, allowedIds);
};

export const buildScopedOrganizationPickerTree = ({
  orgTree = [],
  reportingYears = [],
  selectedYears = [],
  companyId,
  hierarchy = "all",
} = {}) => {
  const scopedOrgTree = scopeOrgTreeByReportingYears({
    orgTree,
    reportingYears,
    selectedYears,
    companyId,
  });
  const selectableOrgIds = getScopedReportingYearOrganizationIds({
    reportingYears,
    selectedYears,
    companyId,
  });

  return buildOrganizationPickerTree(
    scopedOrgTree,
    hierarchy,
    selectableOrgIds,
  );
};

export const collectPickerOptionIds = (nodes = []) => {
  const ids = [];

  const visit = (items = []) => {
    items.forEach((node) => {
      const id = getNodeId(node);
      if (id && !node?.isGroup) ids.push(id);
      if (node?.children?.length) visit(node.children);
    });
  };

  visit(nodes);
  return ids;
};
